import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    IFacetRequestOptions,
    ISearchOptions,
    ISearchResult,
    IStudySearchRequest,
    IVocabSearchRequest
} from '@vivli/features/search/infrastructure/interface';
import { SearchIndexEnum } from '@vivli/features/search/infrastructure/enum';
import { ISearchIndexApi, ISearchIndexOptions } from '@vivli/core/infrastructure/interface';
import { useAuthRestApi } from '../common';
import { toSearchFilterSets } from './to-search-filter-sets';

/**
 * Calls the backend search API (DEV-1133).
 *
 * Replaces use-azure-search-options.hook, use-azure-search-api.hook and use-azure-search.hook. Those
 * three between them built an Azure Search URL, appended the query key, and assembled OData filters
 * in the browser. All of that now happens server-side; this sends a structured query and reads the
 * response.
 */
export const useSearchIndex = ({ index }: ISearchIndexOptions): ISearchIndexApi => {
    //called with NO arguments on purpose. useRestApi returns `overrideOptions` verbatim when it is
    //supplied, discarding the default headers, the retry config and the App Insights tracking - and
    //the old search path supplied them, which is why search calls carried no telemetry. Passing
    //nothing takes the default branch and restores it.
    //
    //useAuthRestApi is already the anonymous/authenticated hybrid these endpoints need:
    //getTokenSilent returns null when there is no active account, and useRestApi only attaches the
    //Authorization header when a token comes back. So signed-out visitors search anonymously.
    const { handleGet, handlePost } = useAuthRestApi();

    const toStudyRequest = (options: ISearchOptions): IStudySearchRequest => ({
        searchText: options.searchText,
        assignedAppType: options.assignedAppType,
        filterSets: toSearchFilterSets(options.filterSets),
        facets: options.facets,
        select: options.select,
        //the backend takes a list; the UI has only ever ordered by one field at a time.
        orderBy: options.orderBy ? [options.orderBy] : undefined,
        top: options.top,
        skip: options.skip
    });

    const toVocabRequest = (options: ISearchOptions): IVocabSearchRequest => ({
        searchText: options.searchText,
        select: options.select,
        orderBy: options.orderBy ? [options.orderBy] : undefined,
        top: options.top,
        skip: options.skip,
        excludeDuplicates: options.excludeDuplicates
    });

    const search = <DocumentType, FacetType = void>(options: ISearchOptions): Observable<ISearchResult<DocumentType, FacetType>> => {
        const request = index === SearchIndexEnum.Vocab ? toVocabRequest(options) : toStudyRequest(options);

        return handlePost<ISearchResult<DocumentType, FacetType>>(`/search/${index}`, request);
    };

    /**
     * Facet buckets with no documents.
     *
     * A search for zero rows, which is exactly what this was at the Azure REST layer too - the old
     * code just read the facets off a response it otherwise ignored.
     *
     * Studies only, and it throws rather than degrading: the vocab request shape carries no facets
     * or assignedAppType, so routing a facet request at it would drop both silently and hand the
     * caller an undefined facet object with nothing anywhere reporting a problem. Nothing requests
     * vocab facets today; this makes it a visible error rather than a quiet nothing if something
     * ever does. (DEV-1133 code review.)
     */
    const getFacets = <FacetType>(options: IFacetRequestOptions): Observable<FacetType> => {
        if (index !== SearchIndexEnum.Studies) {
            return throwError(() => new Error(`Facets are only supported on the studies index, not '${index}'.`));
        }

        return search<unknown, FacetType>({
            facets: options.facets,
            assignedAppType: options.assignedAppType,
            top: 0
        }).pipe(map((result) => result.facets));
    };

    //encodeURIComponent is new, and a genuine fix: the id previously went into the URL unescaped.
    const getDocument = <T>(documentId: string): Observable<T> => handleGet<T>(`/search/${index}/${encodeURIComponent(documentId)}`);

    return {
        search,
        getFacets,
        getDocument
    };
};
